import { useState, useEffect, useRef } from "react";
import { RiAddLargeFill, RiGeminiLine } from "@remixicon/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Logo, LibraryMedia } from "@/types";
import { updateAccountLogos, uploadFilesAsync } from "@/api/globalApiService";
import { updateAccountInList } from "@/screens/utils/Helpers";
import { useGlobalStore } from "@/GlobalStore";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import LoadingSpinner from "@/components/common/loaders/LoadingSpinner";

const MAXIMUM_LOGOS = 5;

type Props = {
  onOpenChange?: (open: boolean) => void;
  onLogoSelect?: (media: LibraryMedia) => void;
};

const BrandLogoHandler = ({ onOpenChange, onLogoSelect }: Props) => {
  const { selectedAccount } = useGlobalStore();
  const [brandLogoPopoverOpen, setBrandLogoPopoverOpen] = useState<boolean>(false);
  const [brandLogos, setBrandLogos] = useState<Logo[]>([]);
  const [isBrandLogoUploading, setIsBrandLogoUploading] = useState<boolean>(false);

  // Sync brand logos from selectedAccount
  useEffect(() => {
    if (selectedAccount?.logos) {
      setBrandLogos(selectedAccount.logos);
    } else {
      setBrandLogos([]);
    }
  }, [selectedAccount?.logos]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle logo upload
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const remainingSlots = MAXIMUM_LOGOS - brandLogos.length;
      const files = Array.from(event.target.files).slice(0, remainingSlots);

      setIsBrandLogoUploading(true);
      try {
        const response = await uploadFilesAsync(files, selectedAccount?.id || "");
        if (response.files?.length) {
          const newLogos: Logo[] = response.files.map((file, index) => ({
            file_id: file.id,
            url: file.public_url,
            type: files[index].type,
          }));
          const updatedLogos = [...brandLogos, ...newLogos];
          await saveLogosToAccount(updatedLogos);
        }
      } catch (error) {
        console.error("Error uploading logos:", error);
      } finally {
        setIsBrandLogoUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  // Save logos to account
  const saveLogosToAccount = async (newLogos: Logo[]) => {
    setBrandLogos(newLogos);
    if (!selectedAccount) return;

    try {
      const res = await updateAccountLogos(
        selectedAccount.id,
        newLogos.map((logo) => logo.file_id),
      );

      // Update global store
      const { setSelectedAccount, accountList, setAccountList } =
        useGlobalStore.getState();
      const updatedAccount = {
        ...selectedAccount,
        logos: res.logos,
      };
      setSelectedAccount(updatedAccount);
      updateAccountInList(accountList, updatedAccount, setAccountList);
    } catch (error) {
      console.error("Error updating brand logos:", error);
      // Revert on error
      if (selectedAccount?.logos) {
        setBrandLogos(selectedAccount.logos);
      }
    }
  };

  // Handle logo removal
  const handleRemoveLogo = (index: number) => {
    const updatedLogos = brandLogos.filter((_, i) => i !== index);
    void saveLogosToAccount(updatedLogos);
  };

  // Handle logo selection for attachment
  const handleLogoSelect = (logo: Logo) => {
    if (onLogoSelect) {
      // Convert Logo to LibraryMedia format
      const libraryMedia: LibraryMedia = {
        id: logo.file_id,
        file_id: logo.file_id,
        file_name: `logo-${logo.file_id}`,
        file_type: logo.type || "image/png",
        file_url: logo.url,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      onLogoSelect(libraryMedia);
      setBrandLogoPopoverOpen(false);
      onOpenChange?.(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setBrandLogoPopoverOpen(open);
    onOpenChange?.(open);
  };

  return (
    <Popover open={brandLogoPopoverOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            setBrandLogoPopoverOpen(true);
            onOpenChange?.(true);
          }}
          className="shrink-0 flex h-7 px-2 py-1 gap-2 items-center self-stretch cursor-pointer text-[12px] rounded-[6px] hover:bg-hover-gray"
        >
          <RiGeminiLine size={14} className="text-brand-primary-400 shrink-0" />
          <span className="text-brand-primary-800 text-[12px] font-normal">
            Brand Logo
          </span>
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        alignOffset={-4}
        sideOffset={-32}
        className="min-w-[307px] w-auto flex flex-col gap-[18px] p-[12px] bg-white shadow-lg rounded-[16px] border-0 squircle squircle-radius-[16px] squircle-smooth-[1] squircle-outline-[1px] squircle-outline-color-border-gray"
      >
        <div className="flex items-center justify-start">
          <div className="flex items-center gap-[8px]">
            <div className="rounded-[8px] bg-[rgba(246,246,246,0.80)] flex w-8 h-8 justify-center items-center gap-[6px]">
              <RiGeminiLine size={14} className="text-brand-primary-600 shrink-0" />
            </div>
            <span className="text-[12px] font-normal text-brand-primary-new tracking-[0.24px]">
              Brand Logo
            </span>
          </div>
        </div>

        {/* Logo Upload Area */}
        <div className="flex flex-wrap gap-[12px]">
          {brandLogos.map((logo, index) => (
            <div
              key={`${logo.file_id}-${index}`}
              className="relative border-[#E7E9EF] border-[1px] rounded-[8px] group bg-white"
            >
              <button
                onClick={() => handleLogoSelect(logo)}
                className="h-[50px] w-[50px] flex items-center justify-center rounded-[6px] overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                title="Click to attach this logo"
              >
                <img
                  crossOrigin="anonymous"
                  src={logo.url}
                  alt={`Logo ${index + 1}`}
                  className="w-[50px] rounded-[8px] object-contain"
                  loading="lazy"
                />
              </button>
              <div className="absolute top-0 right-0 translate-x-[50%] translate-y-[-50%] opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <Button
                  className="p-0 h-[20px] w-[20px] flex items-center justify-center rounded-full bg-[#09090B80] hover:bg-[#09090B] border-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveLogo(index);
                  }}
                >
                  <X size={13} color="#fff" className="shrink-0" />
                </Button>
              </div>
            </div>
          ))}

          {brandLogos.length < MAXIMUM_LOGOS && (
            <label
              htmlFor="brand-logo-upload"
              className="flex items-center justify-center w-[50px] h-[50px] rounded-[6px] bg-[#F6F6F6] hover:bg-gray-200 cursor-pointer transition-colors"
              tabIndex={0}
            >
              <input
                id="brand-logo-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  void handleLogoUpload(e);
                }}
                ref={fileInputRef}
                disabled={isBrandLogoUploading}
                multiple
              />
              {isBrandLogoUploading ? (
                <LoadingSpinner size={14} className="text-brand-orange" />
              ) : (
                <RiAddLargeFill
                  size={14}
                  className="text-brand-primary-600 group-hover:text-brand-primary-800 transition-colors"
                />
              )}
            </label>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default BrandLogoHandler;
